// eslint-disable-next-line filenames/match-exported
import { PortalProvider } from '@gorhom/portal';
import { BrowserTracing } from '@sentry/tracing';
import MixpanelBrowser from 'mixpanel-browser';
import React, { useEffect, useState } from 'react';
import { setCustomText, setCustomTextInput } from 'react-native-global-props';
import { BrowserRouter as WebRouter } from 'react-router-dom';
import * as Sentry from 'sentry-expo';

import { createCookie, deleteCookie, getCookie } from 'shared/utils/cookie';
import eventBus from 'shared/utils/event-bus';
import Mixpanel from 'shared/vendor/Mixpanel';
import ApolloClient, { ApolloProvider, useCustomApolloLink } from 'web/apollo';
import Navigation from 'web/navigation';
import AppsFlyer from 'web/tracking/appsflyer';
import GoogleAnalytics from 'web/tracking/google-analytics';

const GLOBAL_FONT_RULE =
  '"Hiragino Sans", "Segoe UI", Roboto, Arial, sans-serif';

/** Set the font-style globally */
setCustomText({
  allowFontScaling: false,
  style: {
    fontFamily: GLOBAL_FONT_RULE,
    fontSize: 16,
    color: '#2A2E32',
  },
});

setCustomTextInput({
  style: {
    fontFamily: GLOBAL_FONT_RULE,
  },
});

const trackAppSession = () => {
  // Mixpanel Web, unlike Mobile, does not track App Sessions automatically.
  // So we need to implement it manually.
  // https://help.mixpanel.com/hc/en-us/articles/115004596186#mobile-events
  // In Mobile, App Session starts after 10 seconds by default. We replicate that behavior here.

  let sessionStartTimeStamp = 0;
  let timeout = 0;
  const event = '$ae_session'; // This is the database name of "App Session", and will show up as such on the dashboard.

  const startTracking = () => {
    // https://github.com/Microsoft/TypeScript/issues/30128#issuecomment-807394387
    timeout = +global.setTimeout(() => {
      sessionStartTimeStamp = Date.now();
      Mixpanel.timeEvent(event);
    }, 10000);
  };

  document.addEventListener('visibilitychange', () => {
    // Always make sure that the setTimeout is reset whenever the page focus changes
    clearTimeout(timeout);

    // Do only if App Session actually has started
    if (document.visibilityState === 'hidden' && sessionStartTimeStamp) {
      // We get the duration in seconds to the nearest 10th decimal place.
      // Something like 10.1 seconds
      const duration =
        Math.ceil((Date.now() - sessionStartTimeStamp) / 100) / 10;

      /**
       * Tells MixPanel that user left the Web app, e.g. by:
       * 1. Closing tab
       * 2. Navigating to another page or reloading
       * 3. User changed to another tab or window
       * 4. Minimized the window
       * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
       */
      Mixpanel.trackEvent(
        event,
        {
          'Session Length': duration.toString(),
        },
        { transport: 'sendBeacon' }
      );
    } else {
      startTracking();
    }
  });

  startTracking();
};

/**
 * WEB_ENV is defined only during deployments
 * so define it if you want this code block to be run locally
 */
if (process.env.WEB_ENV) {
  const isProd = process.env.WEB_ENV === 'production';

  /** Mixpanel stuff */
  // We want to use the browser instance of Mixpanel
  Mixpanel.setInstance(MixpanelBrowser);

  // Set the Mixpanel token
  Mixpanel.initialize(
    // We need to move these keys somewhere
    isProd
      ? 'ed67017e77857eaee53344b82d3f4293'
      : '5b78e2538205d2e9599a651a3da11dc5',
    !isProd // enable debug mode in staging
  );

  // Set the super properties
  Mixpanel.registerSuperProperties({
    Platform: 'Web',
    Product: 'Paidy Web 2.0',
    'JS Version': process.env.APP_VERSION,
  });

  trackAppSession();

  /** Sentry stuff */
  Sentry.Browser.init({
    release: `paidy-app-rn-web@${process.env.APP_VERSION}`,
    dsn:
      'https://95d2f3934beb442fad6af98c3f4228fa@o56970.ingest.sentry.io/6327384',
    integrations: [new BrowserTracing()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });

  /** AppsFlyer stuff */
  AppsFlyer();

  /** Google Analytics */
  GoogleAnalytics();
}

const COOKIE_NAME = 'isAppSignedIn';

const AppRoot = () => {
  useCustomApolloLink();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isImpersonation, setIsImpersonation] = useState(false);

  useEffect(() => {
    if (!isLoaded) {
      // Render the app only after determining the isAuthenticated state
      // to prevent the app from flickering
      setIsLoaded(true);
    } else {
      if (isAuthenticated) {
        if (!isImpersonation && !getCookie(COOKIE_NAME)) {
          // When the app is already loaded and the consumer signed in, create the cookie
          // The action dispatch happens at the LoginScreen
          createCookie(COOKIE_NAME, 'true', 30);
        }
      } else {
        // This scenario is for when the app is loaded and either:
        // 1. The consumer clicked the sign-out button
        // 2. The refreshToken expired and will need the consumer to sign in again
        if (getCookie(COOKIE_NAME)) {
          deleteCookie(COOKIE_NAME);
        }
      }
    }
  }, [isLoaded, isAuthenticated]);

  // At first load, check if there is a cookie saved
  // and then check the value of the cookie to see if app is already signed in
  useEffect(() => {
    eventBus.on('auth:set', (status: boolean, impersonating = false) => {
      setIsImpersonation(impersonating);
      setIsAuthenticated(status);

      setIsLoaded(false);
    });

    getCookie(COOKIE_NAME) === 'true' && setIsAuthenticated(true);
  }, []);

  return isLoaded ? <Navigation authenticated={isAuthenticated} /> : null;
};

/** The AppRoot with Apollo provider */
const App = () => {
  return (
    <ApolloProvider client={ApolloClient}>
      <PortalProvider>
        <WebRouter>
          <AppRoot />
        </WebRouter>
      </PortalProvider>
    </ApolloProvider>
  );
};

export default App;
